import { useCallback, useRef } from "react";

/** Kéo quá số px này mới tính là kéo, ít hơn vẫn là bấm */
const THRESHOLD = 5;

/**
 * Hàng cuộn ngang (chip danh mục…): giữ chuột trái và kéo sang trái / phải để cuộn.
 * Chỉ áp dụng cho chuột; cảm ứng đã tự cuộn được. Kéo xong thì bỏ cú click ngay sau đó
 * để không chọn nhầm chip đang nằm dưới con trỏ.
 * Trả về ref callback để gắn vào phần tử có overflow-x-auto.
 */
export default function useDragScroll<T extends HTMLElement>() {
  const cleanup = useRef<() => void>();

  // Phần tử bị gỡ thì React gọi lại ref với null nên tự dọn listener ở đây
  // (không dọn trong useEffect: StrictMode chạy lại effect nhưng không gắn lại ref, listener sẽ mất)
  return useCallback((el: T | null) => {
    cleanup.current?.();
    cleanup.current = undefined;

    if (!el) return;

    let startX = 0;
    let startLeft = 0;
    let pointerId: number | null = null;
    let dragging = false;

    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0 || el.scrollWidth <= el.clientWidth) return;

      pointerId = e.pointerId;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      dragging = false;
    };

    const onMove = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;

      const dx = e.clientX - startX;

      if (!dragging) {
        if (Math.abs(dx) < THRESHOLD) return;

        dragging = true;
        // bắt con trỏ sau khi đã kéo (bắt ngay lúc nhấn thì click thường không tới được chip)
        el.setPointerCapture(e.pointerId);
        el.dataset.dragging = "";
      }

      el.scrollLeft = startLeft - dx;
    };

    const onUp = (e: PointerEvent) => {
      if (pointerId !== e.pointerId) return;

      pointerId = null;

      if (!dragging) return;

      delete el.dataset.dragging;

      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);

      // click sinh ra ngay sau khi thả chuột
      const block = (ev: MouseEvent) => {
        ev.stopPropagation();
        ev.preventDefault();
      };

      el.addEventListener("click", block, { capture: true, once: true });
      setTimeout(() => el.removeEventListener("click", block, { capture: true }), 0);
    };

    // chặn kéo ảnh / bôi đen chữ của trình duyệt khi đang kéo
    const onDragStart = (e: DragEvent) => e.preventDefault();

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onUp);
    el.addEventListener("dragstart", onDragStart);

    cleanup.current = () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onUp);
      el.removeEventListener("dragstart", onDragStart);
    };
  }, []);
}
